import { useCallback, useEffect, useState } from 'react';
import {
  ArrowUpRight,
  TrendingUp,
  Users,
  AlertTriangle,
  Smartphone,
  ShoppingCart,
  Activity,
  Wallet,
  Receipt,
  UserRound,
  Clock3,
  UtensilsCrossed,
  Store,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import { getDashboard, type DashboardResponse } from '@/lib/operatorApi';
import { formatKop, formatNumber, formatDate, relativeTime } from '@/lib/format';

type Preset = '7' | '30' | 'month' | 'custom';

// Локальная дата YYYY-MM-DD (без сдвига часового пояса, как в input type=date).
function isoDateLocal(d: Date): string {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}
function daysAgo(n: number): Date {
  return new Date(Date.now() - n * 86400_000);
}
function startOfMonth(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Диапазон для «периодных» метрик. Дефолт — последние 30 дней (совпадает с бэкендом).
  const [fromDate, setFromDate] = useState(() => isoDateLocal(daysAgo(30)));
  const [toDate, setToDate] = useState(() => isoDateLocal(new Date()));
  const [preset, setPreset] = useState<Preset>('30');

  const fetchData = useCallback((from: string, to: string) => {
    setLoading(true);
    setError(null);
    getDashboard({
      from: `${from}T00:00:00.000Z`,
      to: `${to}T23:59:59.999Z`,
    })
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData(fromDate, toDate);
    // первичная загрузка — с дефолтным диапазоном (30 дней)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (p: Preset) => {
    let from = fromDate;
    let to = isoDateLocal(new Date());
    if (p === '7') from = isoDateLocal(daysAgo(6));
    else if (p === '30') from = isoDateLocal(daysAgo(29));
    else if (p === 'month') from = isoDateLocal(startOfMonth());
    setPreset(p);
    if (p !== 'custom') {
      setFromDate(from);
      setToDate(to);
      fetchData(from, to);
    }
  };

  const k = data?.kpis;
  const rangeValid = fromDate <= toDate;

  return (
    <div>
      <PageHeader
        eyebrow="Обзор"
        title="Состояние платформы"
        description="Ключевые метрики, свежие арендаторы и открытые обращения поддержки."
      />

      <div className="page-pad">
        {error && (
          <div className="mb-6 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            Ошибка загрузки: {error}
          </div>
        )}

        {/* ── Снимок «сейчас» ── */}
        <div className="grid grid-cols-2 gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            label="MRR"
            value={k ? formatKop(k.mrr) : '—'}
            hint={k ? `Активных подписок: ${formatNumber(k.active)}` : 'Активные подписки'}
            icon={TrendingUp}
            loading={loading}
          />
          <Kpi
            label="Активные арендаторы"
            value={k ? formatNumber(k.active) : '—'}
            hint={k ? `${k.trial} на trial · всего ${formatNumber(k.totalTenants)}` : ''}
            icon={Users}
            loading={loading}
          />
          <Kpi
            label="Просроченные платежи"
            value={k ? formatNumber(k.pastDue) : '—'}
            hint="Требуют внимания"
            icon={AlertTriangle}
            accent="red"
            loading={loading}
          />
          <Kpi
            label="Устройства онлайн"
            value={k ? formatNumber(k.devicesOnline) : '—'}
            hint={k ? `Всего касс: ${formatNumber(k.devicesTotal)}` : ''}
            icon={Smartphone}
            loading={loading}
          />
        </div>

        <h2 className="mt-8 mb-3 text-[12px] font-medium uppercase tracking-wide text-ink/50">
          Платформа в целом · сейчас
        </h2>
        <div className="grid grid-cols-2 gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            label="Заказов всего"
            value={k ? formatNumber(k.ordersTotal) : '—'}
            hint="За всё время"
            icon={ShoppingCart}
            loading={loading}
          />
          <Kpi
            label="Активные заказы"
            value={k ? formatNumber(k.ordersActive) : '—'}
            hint="Сейчас в работе"
            icon={Activity}
            loading={loading}
          />
          <Kpi
            label="Пользователи"
            value={k ? formatNumber(k.usersTotal) : '—'}
            hint="Все роли платформы"
            icon={UserRound}
            loading={loading}
          />
          <Kpi
            label="Устройства касс"
            value={k ? formatNumber(k.devicesTotal) : '—'}
            hint={k ? `Онлайн: ${formatNumber(k.devicesOnline)}` : ''}
            icon={Store}
            loading={loading}
          />
          <Kpi
            label="Открытые смены"
            value={k ? formatNumber(k.openShifts) : '—'}
            hint="Заведения с активной кассой"
            icon={Clock3}
            loading={loading}
          />
          <Kpi
            label="Блюд в каталогах"
            value={k ? formatNumber(k.dishesTotal) : '—'}
            hint="Суммарно по всем меню"
            icon={UtensilsCrossed}
            loading={loading}
          />
        </div>

        {/* ── За период (зависит от диапазона) ── */}
        <div className="mt-8 mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-[12px] font-medium uppercase tracking-wide text-ink/50">
              За период
            </h2>
            <p className="mt-1 text-[13px] text-ink/60">
              {data
                ? `${formatDate(data.range.from)} — ${formatDate(data.range.to)}`
                : 'Загрузка диапазона…'}
            </p>
          </div>
          <DateRangeBar
            preset={preset}
            fromDate={fromDate}
            toDate={toDate}
            rangeValid={rangeValid}
            onPreset={applyPreset}
            onFrom={(v) => { setPreset('custom'); setFromDate(v); }}
            onTo={(v) => { setPreset('custom'); setToDate(v); }}
            onApply={() => rangeValid && fetchData(fromDate, toDate)}
            loading={loading}
          />
        </div>
        <div className="grid grid-cols-2 gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
          <Kpi
            label="Новые арендаторы"
            value={k ? formatNumber(k.newTenants) : '—'}
            hint="Зарегистрировались за период"
            icon={Sparkles}
            loading={loading}
          />
          <Kpi
            label="Заказы за период"
            value={k ? formatNumber(k.ordersInRange) : '—'}
            hint="По всем заведениям"
            icon={ShoppingCart}
            loading={loading}
          />
          <Kpi
            label="Оборот заведений"
            value={k ? formatKop(k.gmvInRange) : '—'}
            hint="GMV гостевых платежей"
            icon={Wallet}
            loading={loading}
          />
          <Kpi
            label="Выручка платформы"
            value={k ? formatKop(k.revenueInRange) : '—'}
            hint="Успешные платежи по подпискам"
            icon={TrendingUp}
            loading={loading}
          />
          <Kpi
            label="Средний платёж"
            value={k ? formatKop(k.avgPaymentInRange) : '—'}
            hint="Средний гостевой чек"
            icon={Receipt}
            loading={loading}
          />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="card lg:col-span-3">
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <h2 className="text-[14px] font-medium text-ink">Новые арендаторы</h2>
              <Link
                to="/tenants"
                className="inline-flex items-center gap-1 text-[12px] text-ink/60 hover:text-ink"
              >
                Все арендаторы
                <ArrowUpRight size={13} strokeWidth={1.75} />
              </Link>
            </div>
            {/* Desktop: таблица */}
            <div className="hidden md:block">
              <table className="table-grid">
                <thead>
                  <tr>
                    <th>Заведение</th>
                    <th>Город</th>
                    <th>Статус</th>
                    <th className="text-right">Создано</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.latestTenants ?? []).map((t) => (
                    <tr key={t.id}>
                      <td>
                        <Link to={`/tenants/${t.id}`} className="font-medium hover:underline">
                          {t.name}
                        </Link>
                        {t.ownerEmail && (
                          <div className="text-[11px] text-ink/50">{t.ownerEmail}</div>
                        )}
                      </td>
                      <td className="text-ink/70">{t.city ?? '—'}</td>
                      <td>
                        <StatusBadge status={t.status} />
                      </td>
                      <td className="text-right text-ink/60 font-mono text-[12px]">
                        {relativeTime(t.createdAt)}
                      </td>
                    </tr>
                  ))}
                  {!loading && (data?.latestTenants ?? []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-[13px] text-ink/50">
                        Арендаторов пока нет
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile: карточки */}
            <ul className="divide-y divide-line md:hidden">
              {(data?.latestTenants ?? []).map((t) => (
                <li key={t.id} className="px-5 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link
                        to={`/tenants/${t.id}`}
                        className="block truncate text-[14px] font-medium text-ink hover:underline"
                      >
                        {t.name}
                      </Link>
                      {t.ownerEmail && (
                        <div className="truncate text-[11px] text-ink/50">{t.ownerEmail}</div>
                      )}
                      <div className="mt-1 text-[11px] text-ink/60">
                        {t.city ?? '—'} · {relativeTime(t.createdAt)}
                      </div>
                    </div>
                    <StatusBadge status={t.status} />
                  </div>
                </li>
              ))}
              {!loading && (data?.latestTenants ?? []).length === 0 && (
                <li className="px-5 py-8 text-center text-[13px] text-ink/50">
                  Арендаторов пока нет
                </li>
              )}
            </ul>
          </div>

          <div className="card lg:col-span-2">
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <h2 className="text-[14px] font-medium text-ink">Открытые обращения</h2>
              <Link
                to="/support"
                className="inline-flex items-center gap-1 text-[12px] text-ink/60 hover:text-ink"
              >
                Все
                <ArrowUpRight size={13} strokeWidth={1.75} />
              </Link>
            </div>
            <ul className="divide-y divide-line">
              {(data?.openTickets ?? []).map((t) => (
                <li key={t.id} className="px-5 py-4 hover:bg-paper">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-medium text-ink">
                        {t.subject}
                      </div>
                      <div className="mt-1 text-[11px] text-ink/50">
                        {t.tenantName ?? 'Без привязки'} · канал {t.channel.toLowerCase()}
                      </div>
                    </div>
                    <span className="shrink-0 font-mono text-[11px] text-ink/50">
                      {relativeTime(t.lastMessageAt)}
                    </span>
                  </div>
                </li>
              ))}
              {!loading && (data?.openTickets ?? []).length === 0 && (
                <li className="px-5 py-6 text-center text-[13px] text-ink/50">
                  Открытых обращений нет
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// Панель выбора диапазона: пресеты + два date-инпута (произвольный период).
function DateRangeBar({
  preset,
  fromDate,
  toDate,
  rangeValid,
  onPreset,
  onFrom,
  onTo,
  onApply,
  loading,
}: {
  preset: Preset;
  fromDate: string;
  toDate: string;
  rangeValid: boolean;
  onPreset: (p: Preset) => void;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
  onApply: () => void;
  loading: boolean;
}) {
  const presets: { key: Preset; label: string }[] = [
    { key: '7', label: '7 дней' },
    { key: '30', label: '30 дней' },
    { key: 'month', label: 'Этот месяц' },
  ];
  return (
    <div className="flex flex-col gap-2 md:items-end">
      <div className="inline-flex border border-line">
        {presets.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`px-3 py-1.5 text-[12px] ${
              preset === p.key ? 'bg-ink text-white' : 'text-ink/70 hover:text-ink'
            }`}
            onClick={() => onPreset(p.key)}
            disabled={loading}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1.5">
        <input
          type="date"
          className="input font-mono text-[12px] py-1"
          value={fromDate}
          max={toDate}
          onChange={(e) => onFrom(e.target.value)}
        />
        <span className="text-ink/40">—</span>
        <input
          type="date"
          className="input font-mono text-[12px] py-1"
          value={toDate}
          min={fromDate}
          onChange={(e) => onTo(e.target.value)}
        />
        <button
          type="button"
          className="btn-outline py-1"
          onClick={onApply}
          disabled={loading || !rangeValid}
          title={rangeValid ? 'Применить диапазон' : 'Начало должно быть ≤ конца'}
        >
          Показать
        </button>
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  icon: Icon,
  accent,
  loading,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  accent?: 'red';
  loading?: boolean;
}) {
  return (
    <div className="bg-white p-6">
      <div className="flex items-center justify-between">
        <span className="kpi-label">{label}</span>
        <Icon
          size={16}
          strokeWidth={1.5}
          className={accent === 'red' ? 'text-red-600' : 'text-ink/40'}
        />
      </div>
      <div className={`mt-4 kpi-value ${loading ? 'text-ink/30' : ''}`}>{value}</div>
      <div className="mt-2 text-[12px] text-ink/50">{hint || '\u00A0'}</div>
    </div>
  );
}
